<!-- src/apps/session/views/PasswordReset.vue -->

<script setup lang="ts">
  import AuthView from '@/apps/session/components/AuthView.vue';
  import PasswordStrengthMeter from '@/apps/session/components/PasswordStrengthMeter.vue';
  import { useAuth } from '@/shared/composables/useAuth';
  import { ref, computed, onBeforeUnmount } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRouter } from 'vue-router';

  export interface Props {
    enabled?: boolean;
    resetKey: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    enabled: true,
    resetKey: '',
  });

  const { t } = useI18n();
  const router = useRouter();
  const { resetPassword, isLoading, error, fieldError, clearErrors } = useAuth();

  // Backend-enforced minimum (Rodauth `password_minimum_length 8`). Kept in one
  // place so the <input minlength> and the strength meter stay in sync.
  const MIN_PASSWORD_LENGTH = 8;

  // Delay before the automatic redirect to /signin after a successful reset.
  const REDIRECT_DELAY_MS = 3000;

  const newPassword = ref('');
  const confirmPassword = ref('');
  const resetSucceeded = ref(false);
  let redirectTimer: ReturnType<typeof setTimeout> | null = null;

  const hasValidResetKey = computed(() => props.resetKey && props.resetKey.trim() !== '');

  // Rodauth returns reset errors as raw English strings. Map the known ones to
  // pt_BR so a Brazilian user never sees English; anything unmapped falls back
  // to a localized generic message.
  const RESET_ERROR_I18N: Record<string, string> = {
    'There was an error resetting your password': 'web.auth.password_reset.error_generic',
  };
  const localizedError = computed(() => {
    const raw = error.value;
    if (!raw) return '';
    const key = RESET_ERROR_I18N[raw.trim()];
    return key ? t(key) : t('web.auth.password_reset.error_generic');
  });

  // The field-error value 'invalid password' is Rodauth's DELIBERATELY generic
  // message for "same as current password" (account_management.rb keeps it
  // generic so the reset flow is not a password-reuse oracle). Translate it to
  // an equally generic pt_BR string — never reveal that it matched the current
  // password. The standing hint on the form teaches the rule without tying it
  // to a submission.
  const FIELD_VALUE_I18N: Record<string, string> = {
    'invalid password': 'web.auth.password_reset.password_invalid',
  };
  const localizedFieldValue = computed(() => {
    const value = fieldError.value?.[1];
    if (!value) return '';
    const key = FIELD_VALUE_I18N[value.trim()];
    return key ? t(key) : value;
  });

  const handleSubmit = async () => {
    if (!hasValidResetKey.value) {
      return;
    }
    clearErrors();
    const ok = await resetPassword(props.resetKey, newPassword.value, confirmPassword.value);
    if (ok) {
      // On-page success message (pt_BR) instead of the English backend toast;
      // schedule the redirect to the sign-in page. The toast is suppressed in
      // useAuth.resetPassword specifically for this flow.
      resetSucceeded.value = true;
      redirectTimer = setTimeout(() => {
        router.push('/signin');
      }, REDIRECT_DELAY_MS);
    }
  };

  onBeforeUnmount(() => {
    if (redirectTimer) {
      clearTimeout(redirectTimer);
      redirectTimer = null;
    }
  });
</script>

<template>
  <AuthView
    :heading="t('web.auth.password_reset.title')"
    heading-id="password-reset-heading"
    :with-subheading="false"
    :hide-icon="true"
    :show-return-home="false">
    <template #form>
      <!-- Success state: inline confirmation + auto-redirect (no toast) -->
      <div
        v-if="resetSucceeded"
        class="reset-success flex flex-col items-center gap-3 py-4 text-center"
        role="status"
        aria-live="polite"
        data-testid="password-reset-success">
        <span
          class="flex size-10 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400"
          aria-hidden="true">
          ✓
        </span>
        <p class="font-mono text-sm font-bold text-day-ink dark:text-night-ink">
          {{ t('web.auth.password_reset.success.title') }}
        </p>
        <p class="text-xs text-day-ink-faint dark:text-night-ink-faint">
          {{ t('web.auth.password_reset.success.redirecting') }}
        </p>
      </div>

      <template v-else>
        <p class="mb-2 text-gray-700 dark:text-gray-300">
          {{ t('web.auth.password_reset.description') }}
        </p>
        <!-- Standing rule (not an error): teaches "must differ from current"
             without a per-submission oracle, so a strong-but-reused password
             makes sense to the user. -->
        <p class="mb-6 text-sm text-gray-500 dark:text-gray-400">
          {{ t('web.auth.password_reset.must_differ_hint') }}
        </p>

        <!-- Missing reset key error -->
        <div
          v-if="!hasValidResetKey"
          class="mb-4 rounded-md bg-red-50 p-4 dark:bg-red-900/20"
          role="alert">
          <p class="text-sm text-red-800 dark:text-red-200">
            {{ t('web.auth.password_reset.invalid_key') }}
          </p>
        </div>

        <!-- Error messages -->
        <div
          v-if="error && hasValidResetKey"
          class="mb-4 rounded-md bg-red-50 p-4 dark:bg-red-900/20"
          role="alert"
          aria-live="assertive"
          aria-atomic="true">
          <!-- Generic error message (localized) -->
          <p class="text-sm text-red-800 dark:text-red-200">
            {{ localizedError }}
          </p>
          <!-- Field-specific error (value localized; kept generic on purpose) -->
          <p
            v-if="fieldError"
            id="password-error"
            class="mt-2 text-sm font-medium text-red-800 dark:text-red-200">
            {{ t(`web.auth.field-errors.${fieldError[0]}`) || fieldError[0] }}: {{ localizedFieldValue }}
          </p>
        </div>

        <form
          v-if="hasValidResetKey"
          @submit.prevent="handleSubmit"
          id="passwordResetForm"
          class="space-y-4"
          data-testid="password-reset-form">
          <!-- Username field for accessibility -->
          <div class="hidden">
            <label
              class="mb-2 block text-sm font-bold text-gray-700 dark:text-gray-300"
              for="email">
              {{ t('web.COMMON.email_address') }}
            </label>
            <input
              type="text"
              name="email"
              id="usernameField"
              autocomplete="email"
              class="focus:shadow-outline w-full appearance-none rounded border px-3 py-2 leading-tight text-gray-700 shadow focus:outline-none dark:bg-gray-700 dark:text-gray-300"
              placeholder="" />
          </div>

          <div>
            <label
              class="mb-2 block text-sm font-bold text-gray-700 dark:text-gray-300"
              for="passField">
              {{ t('web.COMMON.new_password') }}
            </label>
            <input
              type="password"
              name="password"
              id="passField"
              required
              :minlength="MIN_PASSWORD_LENGTH"
              :disabled="isLoading"
              autocomplete="new-password"
              :aria-invalid="!!(fieldError && (fieldError[0] === 'password' || fieldError[0] === 'password-confirm'))"
              :aria-describedby="fieldError && (fieldError[0] === 'password' || fieldError[0] === 'password-confirm') ? 'password-error' : undefined"
              class="focus:shadow-outline w-full appearance-none rounded border px-3 py-2 leading-tight text-gray-700 shadow focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-700 dark:text-gray-300"
              placeholder=""
              v-model="newPassword"
              data-testid="password-reset-new-input" />
            <!-- Strength meter reflecting the real backend rule (min length) -->
            <PasswordStrengthMeter
              :password="newPassword"
              :min-length="MIN_PASSWORD_LENGTH" />
          </div>
          <div>
            <label
              class="mb-2 block text-sm font-bold text-gray-700 dark:text-gray-300"
              for="pass2Field">
              {{ t('web.COMMON.confirm_password') }}
            </label>
            <input
              type="password"
              name="password-confirm"
              id="pass2Field"
              required
              :minlength="MIN_PASSWORD_LENGTH"
              :disabled="isLoading"
              autocomplete="new-password"
              :aria-invalid="!!(fieldError && fieldError[0] === 'password-confirm')"
              :aria-describedby="fieldError && fieldError[0] === 'password-confirm' ? 'password-error' : undefined"
              class="focus:shadow-outline w-full appearance-none rounded border px-3 py-2 leading-tight text-gray-700 shadow focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-700 dark:text-gray-300"
              placeholder=""
              v-model="confirmPassword"
              data-testid="password-reset-confirm-input" />
          </div>
          <div class="flex items-center justify-between">
            <button
              type="submit"
              :disabled="isLoading"
              class="focus:shadow-outline rounded bg-brand-500 px-4 py-2 font-bold text-white transition duration-300 hover:bg-brand-700 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:bg-brand-600 dark:hover:bg-brand-800"
              data-testid="password-reset-submit">
              <span v-if="isLoading">{{ t('web.COMMON.processing') || 'Processing...' }}</span>
              <span v-else>{{ t('web.account.changePassword.updatePassword') }}</span>
            </button>
          </div>
        </form>
      </template>
    </template>
    <template #footer>
      <!-- Explicit exit back to the home page (owner asked for a clear "Sair") -->
      <RouterLink
        to="/"
        class="inline-flex items-center gap-1"
        data-testid="password-reset-exit"
        :aria-label="t('web.layout.return_to_home_page')">
        <span aria-hidden="true">←</span>
        <span>{{ t('web.auth.password_reset.exit') }}</span>
      </RouterLink>
    </template>
  </AuthView>
</template>

<style scoped>
  .reset-success {
    animation: reset-success-fade 250ms ease-out;
  }

  @media (prefers-reduced-motion: reduce) {
    .reset-success {
      animation: none;
    }
  }

  @keyframes reset-success-fade {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
</style>
